import { FunctionComponent, useCallback, useEffect, useState } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import styled, { css, useTheme } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import translate from '@i18n'
import SearchPageContext from '@contexts/searchPageContext'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import JSONRenderer from '@components/JSONRenderer'
import SearchComponents from '@components/Search'
import AISearch from '@components/Search/AISearch/AISearch'
import EnhancedSearchResultsHeader from '@components/Search/EnhancedSearchResultsHeader'
import SvgIcon from '@components/SvgIcon'

import {
  EnhancedSearchResponseAPIProps,
  SearchResponseAPIProps,
  SearchResultsData,
} from './types'
import {
  enrichListItemTeasers,
  getResultNumberRange,
  getResults,
} from './utils'

const { Container, ResultsContainer, ResultsHeader } = SearchComponents

const ErrorMessage = styled.div`
  ${({
    theme: {
      typography: {
        bodycopy: {
          medium2: { bundledCSS: medium2BodycopyCSS },
        },
      },
      spacing: { spacing40 },
    },
  }) => css`
    ${medium2BodycopyCSS};
    margin-bottom: ${spacing40};
  `}
`

const StyledPrimaryCTAButton = styled(PrimaryCTAButton)`
  ${({
    theme: {
      spacing: { spacing56 },
    },
  }) => css`
    width: 100%;
    margin-top: ${spacing56};
    ${desktopCSS(css`
      margin-top: 68px;
    `)}
  `}
`

const rangeZeroResult = getResultNumberRange(0)

const EnhancedSearch: FunctionComponent = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const queryClient = useQueryClient()
  const theme = useTheme()

  const onNoResultTrack = useCallback<
    TrackingFnType<{ searchTerm: string | undefined }>
  >(({ extraData }) => {
    const { searchTerm } = extraData
    return {
      event: 'search_zero_result',
      eventCategory: 'search',
      eventLabel: searchTerm,
      eventAction: 'zero_result',
      search_term: searchTerm?.toLowerCase(),
      q_search_result_range: rangeZeroResult,
    }
  }, [])
  const trackingOnSearch = useCallback<TrackingFnType<{ searchTerm: string }>>(
    ({ extraData }) => {
      const { searchTerm } = extraData
      return {
        event: 'search',
        eventCategory: 'search',
        eventLabel: searchTerm.toLowerCase(),
        eventAction: 'submitted',
        search_term: searchTerm.toLowerCase(),
      }
    },
    [],
  )

  const handleSearchTrack = useTracking(trackingOnSearch)

  const noResultTrack = useTracking(onNoResultTrack)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery<
    SearchResponseAPIProps,
    unknown,
    EnhancedSearchResponseAPIProps,
    ['searchData', string],
    number
  >({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['searchData', searchTerm],
    queryFn: ({ pageParam = 0 }) =>
      getResults(searchTerm, pageParam, noResultTrack),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.total_pages - 1
        ? lastPage.page + 1
        : undefined
    },
    select: (data): EnhancedSearchResponseAPIProps => {
      const transformedData = data.pages.flatMap((page) =>
        enrichListItemTeasers(page.content, true),
      )
      return {
        ...data,
        transformed_results: transformedData,
        total_elements: data.pages?.[0]?.total_elements,
        total_pages: data.pages?.[0]?.total_pages,
      }
    },
    enabled: !!searchTerm,
    gcTime: 0,
  })
  const resultsAvailable =
    !isLoading && !isError && data && data.transformed_results?.length > 0
  const transformedResults = data?.transformed_results || []
  const totalElements: number = data?.total_elements || 0
  const totalPages: number = data?.total_pages || 0

  const onQuestionSubmit = (value: string) => {
    const trimmedSearchTerm = value.trim()

    if (trimmedSearchTerm !== '' && trimmedSearchTerm !== searchTerm) {
      handleSearchTrack({ searchTerm: trimmedSearchTerm })
    }

    setSearchTerm(trimmedSearchTerm)
  }

  useEffect(() => {
    queryClient.setQueryData<SearchResultsData>(['searchResults'], {
      totalElements,
      totalPages,
      searchResultRange: getResultNumberRange(totalElements || 0),
      searchTerm,
    })

    return () => {
      queryClient.removeQueries({ queryKey: ['searchResults'], exact: true })
    }
  }, [queryClient, searchTerm, totalElements, totalPages])

  return (
    <SearchPageContext.Provider value={true}>
      <Container>
        <AISearch onQuestionSubmit={onQuestionSubmit} />
        {searchTerm && (
          <ResultsHeader>
            <EnhancedSearchResultsHeader
              searchTerm={searchTerm}
              isLoading={isLoading}
              totalElements={totalElements}
            />
          </ResultsHeader>
        )}
        {searchTerm && (
          <ResultsContainer>
            {((!resultsAvailable && !isLoading) || isError) && (
              <ErrorMessage>
                {translate(
                  isError
                    ? 'search.errorMessage'
                    : 'search.noResultsExplanation',
                )}
              </ErrorMessage>
            )}
            <JSONRenderer>{transformedResults}</JSONRenderer>
          </ResultsContainer>
        )}
        {hasNextPage && (
          <StyledPrimaryCTAButton
            type="button"
            size="large"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {translate('enhancedSearch.loadButton')}
            <SvgIcon
              iconName="chevron-down"
              size={24}
              color={theme.colors.iconDefault}
            />
          </StyledPrimaryCTAButton>
        )}
      </Container>
    </SearchPageContext.Provider>
  )
}

export default EnhancedSearch
